import { useMutation, useQueries, useQuery } from '@tanstack/react-query';

import { GUID, InternalUserProfile, Tenant, TenantTeamMember } from '@ndvr/auth-api';
import { AdHocMeetingRequest } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { allTenantTeamMembers, tenantTeamQueryOptions } from 'views/admin/tenantTeam/tenant-team-queries';
import { Toast } from 'widgets';

interface InviteClaireParams {
    organizerProfileGUID: GUID<InternalUserProfile>;
    tenantGUIDs: GUID<Tenant>[];
    request: AdHocMeetingRequest;
}

export const useInviteClaireToAdHocMeeting = () =>
    useMutation({
        mutationFn: async ({ organizerProfileGUID, tenantGUIDs, request }: InviteClaireParams) => {
            await CustomerApi({ hideError: true }).inviteMeetingBotToAdHocMeeting(organizerProfileGUID, request, {
                tenantGUIDs,
            });
        },
        onSuccess: () => Toast.success('Claire is joining the meeting'),
        onError: (error: Error) => Toast.forError(error, 'Failed to invite Claire'),
    });

export const useAdHocMeetingTeamMembers = (
    tenantGUIDs: GUID<Tenant>[],
    excludedProfileGUID?: GUID<InternalUserProfile>,
) => {
    const teamQueries = useQueries({
        queries: tenantGUIDs.map(tenantTeamQueryOptions),
    });
    const connectedProfilesQuery = useQuery({
        queryKey: ['calendar', 'tenant', 'connection', [...tenantGUIDs].sort()],
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [connectedProfileGUIDs] = await CustomerApi().listTeamMembersWithConnectedCalendars(tenantGUIDs);
            return connectedProfileGUIDs ?? [];
        },
    });

    const memberByGUID = new Map<GUID<InternalUserProfile>, TenantTeamMember>();
    teamQueries.forEach(({ data }) => {
        allTenantTeamMembers(data).forEach((member) => {
            if (member.internalUserGUID && !memberByGUID.has(member.internalUserGUID)) {
                memberByGUID.set(member.internalUserGUID, member);
            }
        });
    });
    const connectedProfileGUIDs = new Set(connectedProfilesQuery.data ?? []);
    const teamMembers = [...memberByGUID.values()].filter((member) => member.internalUserGUID !== excludedProfileGUID);

    return {
        data: teamMembers.filter((member) => connectedProfileGUIDs.has(member.internalUserGUID)),
        isPending:
            teamQueries.some((query) => query.isPending) ||
            (tenantGUIDs.length > 0 && connectedProfilesQuery.isPending),
        isError: teamQueries.some((query) => query.isError) || connectedProfilesQuery.isError,
    };
};
